// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Application constants
// ---------------------------------------------------------------------------

// --- Timers & intervals (ms) -----------------------------------------------

/** How often the heartbeat pings the backend. */
export const HEARTBEAT_INTERVAL = 60_000;

/** How often reservations/tables are synced (WAN). */
export const SYNC_INTERVAL = 15_000;

/** Duration (seconds) a table stays locked. */
export const TABLE_LOCK_DURATION = 300;

/** Duration (seconds) of an instant reservation. */
export const INSTANT_RESERVATION_DURATION = 7_200;

// --- Network ---------------------------------------------------------------

/** Maximum time (ms) to wait for an API response before aborting. */
export const API_TIMEOUT = 10_000;

/** JWT device session timeout (8 hours in ms). */
export const SESSION_TIMEOUT = 28_800_000;

/** Margin before token expiry to trigger refresh (1 hour in ms). */
export const TOKEN_REFRESH_MARGIN = 3_600_000;

// --- Payments --------------------------------------------------------------

/** Supported payment types (NF525 compliant). */
export const PAYMENT_TYPES = ['CB', 'ESPECES', 'PREVENTE'] as const;

// --- Device ----------------------------------------------------------------

/** Device type identifier sent to the backend. */
export const DEVICE_TYPE = 'pwa_caisse' as const;

// --- Validation limits -----------------------------------------------------

/** Maximum transaction amount in euros. */
export const MAX_AMOUNT = 9999.99;

/** Minimum PIN length for employee login. */
export const MIN_PIN_LENGTH = 4;

/** Maximum PIN length for employee login. */
export const MAX_PIN_LENGTH = 6;

// --- Scanner ---------------------------------------------------------------

/** Debounce delay (ms) between two consecutive QR scans. */
export const QR_SCAN_DEBOUNCE = 1_000;

// --- Offline / Sync --------------------------------------------------------

/** Interval (ms) to push queued offline transactions. */
export const OFFLINE_SYNC_INTERVAL = 30_000;

/** Maximum number of transactions in the offline queue. */
export const MAX_QUEUE_SIZE = 1_000;

/** Progressive back-off delays (ms) for failed request retries. */
export const RETRY_DELAYS = [1_000, 2_000, 5_000, 10_000, 30_000] as const;

// --- Storage ---------------------------------------------------------------

/** IndexedDB database name. */
export const DB_NAME = 'SIDEVIEW_CaisseDB';

/** IndexedDB schema version. */
export const DB_VERSION = 3;

/** Service Worker cache name. */
export const CACHE_NAME = 'caisse-pwa-v2';

// --- Validation limits (from config.js) ------------------------------------

/** Minimum transaction amount in euros. */
export const MIN_AMOUNT = 0.01;

/** Maximum quantity per line item. */
export const MAX_QUANTITY = 999;

/** Phone number regex for FR mobile. */
export const PHONE_REGEX = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;

/** Table ID format regex. */
export const TABLE_ID_REGEX = /^[A-Za-z0-9_-]+$/;

// --- Feature flags (default set) -------------------------------------------

export const DEFAULT_FEATURES: Record<string, boolean> = {
  MULTI_PAYMENT: true,
  INSTANT_RESERVATION: true,
  TABLE_MANAGEMENT: true,
  QR_SCANNER: true,
  FLOOR_PLANS: true,
  FISCAL_RECEIPT: true,
  OFFLINE_MODE: true,
  PRINT: false,
  SOUND: true,
};

// --- QR Scanner config -----------------------------------------------------

export const QR_SCANNER_CONFIG = {
  PREFERRED_CAMERA: 'environment' as const,
  MAX_SCANS_PER_SECOND: 5,
  FORMATS: ['qr_code', 'ean_13', 'ean_8', 'code_128', 'code_39'] as const,
};
